import { useLocale } from '@/contexts/LocaleContext';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useSearchParams } from 'react-router-dom';
import { useHubTabs } from '@/lib/permisos';
import { Users, LayoutGrid, ListChecks, Search, X, FileText } from 'lucide-react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

// 2026-07-08 — "Asignación" moved to Mi Negocio (between Mi Equipo y
// Catálogo), per explicit request — this is company-wide configuration,
// not a day-to-day CRM working view. See BusinessHubLayout.js.
//
// Sprint 53 (4/N) — "Entrantes" quitado de la navegación: desde ahora TODOS
// los orígenes de leads (WhatsApp, Ads/webhooks, llamadas entrantes)
// convierten a Lead CRM automáticamente, ya no es un paso manual del día a
// día. La pantalla (CrmInboundPending.js) sigue existiendo en
// /tenant/crm/pending para el caso raro de un evento fallido/duplicado que
// de verdad necesite revisión — solo dejó de ocupar un lugar permanente en
// el menú.
//
// 2026-07-28 — fuera la pestaña «Inicio». Era una pantalla de números y cuatro
// botones que llevaban a Pipeline, Leads y Tareas: las mismas pestañas que ya
// estaban justo encima. En la práctica cobraba un clic de peaje para llegar al
// tablero, que es a donde iba todo el mundo. Ahora el Pipeline es lo primero
// que se ve y los números viven en su encabezado (components/crm/CrmResumen).
const TABS = [
  { key: 'pipeline', path: '/tenant/crm', labelKey: 'crm.pipeline', icon: LayoutGrid, end: true, module: 'pipeline' },
  { key: 'leads', path: '/tenant/crm/leads', labelKey: 'crm.leads', icon: Users, module: 'crm' },
  { key: 'tasks', path: '/tenant/crm/tasks', labelKey: 'crm.tasks', icon: ListChecks, module: 'crm' },
  { key: 'plantillas', path: '/tenant/crm/plantillas', labelKey: 'crm.templates', icon: FileText, module: 'crm' },
];

const FILTER_KEYS = ['search', 'date_from', 'date_to', 'assigned_to', 'campaign_name', 'pool_id', 'branch_id'];

function monthStartIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
}
function todayEndIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
}

export default function CrmHubLayout() {
  const { t } = useLocale();
  const { tabs: visibleTabs, sinAcceso } = useHubTabs(TABS);
  const [searchParams, setSearchParams] = useSearchParams();
  const [teamMembers, setTeamMembers] = useState([]);
  const [pools, setPools] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') || '');

  // Filtro de fechas: siempre parte del mes actual si no hay uno guardado en la URL.
  useEffect(() => {
    if (!searchParams.get('date_from') || !searchParams.get('date_to')) {
      const next = new URLSearchParams(searchParams);
      if (!next.get('date_from')) next.set('date_from', monthStartIso());
      if (!next.get('date_to')) next.set('date_to', todayEndIso());
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/tenant/team').catch(() => ({ data: [] })),
      api.get('/tenant/assignment/pools').catch(() => ({ data: { items: [] } })),
      api.get('/tenant/locations').catch(() => ({ data: [] })),
    ]).then(([tRes, pRes, bRes]) => {
      setTeamMembers(Array.isArray(tRes.data) ? tRes.data : tRes.data?.items || []);
      setPools(pRes.data?.items || []);
      setBranches(Array.isArray(bRes.data) ? bRes.data : bRes.data?.items || []);
    });
  }, []);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const submitSearch = () => setFilter('search', searchDraft);

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS.forEach(k => next.delete(k));
    next.set('date_from', monthStartIso());
    next.set('date_to', todayEndIso());
    setSearchDraft('');
    setSearchParams(next, { replace: true });
  };

  const activeFilterCount = FILTER_KEYS.filter(k => k !== 'date_from' && k !== 'date_to' && searchParams.get(k)).length;
  const search = searchParams.toString();

  // Los valores por default (mes actual) se calculan aquí mismo, no solo en
  // el useEffect de arriba — un cliente con años de leads acumulados no
  // puede depender de que la URL se actualice en un segundo render antes de
  // que Leads/Pipeline disparen su primera consulta: React corre los
  // efectos de los hijos ANTES que el de este layout, así que sin este
  // fallback la primera carga siempre salía sin fecha (consulta completa
  // sobre toda la tabla) y una milésima de segundo después se repetía ya
  // filtrada — dos consultas en vez de una, y la primera sin límite.
  const filters = {
    search: searchParams.get('search') || '',
    date_from: searchParams.get('date_from') || monthStartIso(),
    date_to: searchParams.get('date_to') || todayEndIso(),
    assigned_to: searchParams.get('assigned_to') || '',
    campaign_name: searchParams.get('campaign_name') || '',
    pool_id: searchParams.get('pool_id') || '',
    branch_id: searchParams.get('branch_id') || '',
  };
  // Ready-to-append query string built from the active filters, for pages
  // that fetch their own list (GET .../leads?..., .../deals?...).
  const filterQuery = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');

  return (
    <div className="space-y-3" data-testid="crm-hub">
      {/* El título y su explicación en una sola línea: el tablero necesita el
          alto de pantalla más que este bloque. La frase se guarda para
          pantallas anchas, donde cabe sin empujar nada. */}
      <div className="flex items-baseline gap-3 flex-wrap">
        <h1 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
          <Users className="w-5 h-5" /> CRM
        </h1>
        <p className="text-xs text-[#64748B] hidden lg:block">Convierte leads entrantes en oportunidades, muévelas por etapas y registra tareas de seguimiento.</p>
      </div>

      {/* Shared filter bar — applies to Leads y Pipeline por igual (Tareas/Entrantes tienen sus propios filtros) */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 flex flex-wrap items-end gap-2" data-testid="crm-filter-bar">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] text-[#64748B] block mb-0.5">Buscar</label>
          <div className="flex gap-1">
            <Input value={searchDraft} onChange={e => setSearchDraft(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitSearch()}
              placeholder="Nombre, teléfono, canal (ej. whatsapp)..." className="h-8 text-xs" data-testid="crm-filter-search" />
            <Button size="sm" variant="outline" className="h-8 px-2" onClick={submitSearch}><Search className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div className="w-36">
          <label className="text-[10px] text-[#64748B] block mb-0.5">Desde</label>
          <Input type="date" className="h-8 text-xs" value={filters.date_from ? filters.date_from.slice(0, 10) : ''}
            onChange={e => setFilter('date_from', e.target.value ? new Date(e.target.value).toISOString() : '')} data-testid="crm-filter-date-from" />
        </div>
        <div className="w-36">
          <label className="text-[10px] text-[#64748B] block mb-0.5">Hasta</label>
          <Input type="date" className="h-8 text-xs" value={filters.date_to ? filters.date_to.slice(0, 10) : ''}
            onChange={e => setFilter('date_to', e.target.value ? new Date(e.target.value).toISOString() : '')} data-testid="crm-filter-date-to" />
        </div>
        {teamMembers.length > 0 && (
          <div className="w-40">
            <label className="text-[10px] text-[#64748B] block mb-0.5">Vendedor</label>
            <Select value={filters.assigned_to || 'all'} onValueChange={v => setFilter('assigned_to', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="crm-filter-vendor"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-36">
          <label className="text-[10px] text-[#64748B] block mb-0.5">Campaña</label>
          <Input value={filters.campaign_name} onChange={e => setFilter('campaign_name', e.target.value)}
            placeholder="Ej. Promo Verano" className="h-8 text-xs" data-testid="crm-filter-campaign" />
        </div>
        {pools.length > 0 && (
          <div className="w-36">
            <label className="text-[10px] text-[#64748B] block mb-0.5">Grupo</label>
            <Select value={filters.pool_id || 'all'} onValueChange={v => setFilter('pool_id', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="crm-filter-group"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {pools.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {branches.length > 0 && (
          <div className="w-36">
            <label className="text-[10px] text-[#64748B] block mb-0.5">Sucursal</label>
            <Select value={filters.branch_id || 'all'} onValueChange={v => setFilter('branch_id', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="crm-filter-branch"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clearFilters} data-testid="crm-filter-clear">
            <X className="w-3.5 h-3.5 mr-1" /> Limpiar ({activeFilterCount})
          </Button>
        )}
      </div>

      {sinAcceso && (
        <div className="text-sm text-[#64748B] py-6 text-center border border-[#E2E8F0] rounded-xl bg-white">
          No tienes acceso a esta sección. Pídeselo al dueño o administrador.
        </div>
      )}
      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="crm-hub-tabs">
        {visibleTabs.map(tab => (
          <NavLink key={tab.key} to={{ pathname: tab.path, search: search ? `?${search}` : '' }} end={tab.end} data-testid={`crm-tab-${tab.key}`}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActive ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`
            }>
            <tab.icon className="w-4 h-4" /> {tab.labelKey ? t(tab.labelKey) : tab.label}
          </NavLink>
        ))}
      </div>
      <Outlet context={{ filters, filterQuery }} />
    </div>
  );
}
